import { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Folder, Building2, User, Eye, Download, Share2, Pencil, Star, Trash2 } from 'lucide-react';
import { documenti as documentiApi, cantieri as cantieriApi, clienti as clientiApi } from '../../utils/api';
import SkeletonLoader from '../../components/shared/SkeletonLoader';
import CondividiModal from '../../components/documenti/CondividiModal';
import FirmaDigitaleModal from '../../components/documenti/FirmaDigitaleModal';
import CaricaAltri from '../../components/documenti/CaricaAltri';
import useArchivioBlocchi from './useArchivioBlocchi';
import { statoPreferito } from './archivioBlocchi';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

const TIPI = [
  { value: '',             label: 'Tutti i tipi' },
  { value: 'preventivo',   label: 'Preventivo' },
  { value: 'contratto',    label: 'Contratto' },
  { value: 'fattura',      label: 'Fattura' },
  { value: 'foto',         label: 'Foto' },
  { value: 'ddt',          label: 'DDT' },
  { value: 'bolla',        label: 'Bolla' },
  { value: 'certificato',  label: 'Certificato' },
  { value: 'collaudo',     label: 'Collaudo' },
  { value: 'planimetria',  label: 'Planimetria' },
  { value: 'sal',          label: 'SAL' },
  { value: 'computo',      label: 'Computo metrico' },
  { value: 'corrispondenza',label: 'Corrispondenza' },
  { value: 'pdf_generico', label: 'PDF generico' },
  { value: 'altro',        label: 'Altro' },
];

const TIPO_LABEL = Object.fromEntries(TIPI.slice(1).map((t) => [t.value, t.label]));

const TIPO_ICON_COLOR = {
  preventivo:   'var(--ds-danger)',
  contratto:    'var(--ds-danger)',
  pdf_generico: 'var(--ds-danger)',
  foto:         'var(--zone-acqua)',
  ddt:          'var(--zone-blue)',
  bolla:        'var(--zone-blue)',
  altro:        'var(--ds-muted)',
};

const IconFile = ({ size = 18, color = 'currentColor' }) => (<FileText size={size} color={color} strokeWidth={2} />);
const IconImage = ({ size = 18, color = 'currentColor' }) => (<ImageIcon size={size} color={color} strokeWidth={2} />);
const IconFolder = ({ size = 48 }) => (<Folder size={size} color="var(--ds-muted)" strokeWidth={2} />);
const IconBuilding = () => (<Building2 size={14} strokeWidth={2} />);
const IconUser = () => (<User size={14} strokeWidth={2} />);
const IconEye = () => (<Eye size={14} strokeWidth={2} />);
const IconDownload = () => (<Download size={14} strokeWidth={2} />);
const IconLink = () => (<Share2 size={14} strokeWidth={2} />);
const IconPen = () => (<Pencil size={14} strokeWidth={2} />);
const IconStar = ({ filled }) => (<Star size={14} strokeWidth={2} fill={filled ? 'var(--warn)' : 'none'} color={filled ? 'var(--warn)' : 'currentColor'} />);
const IconTrash = () => (<Trash2 size={14} strokeWidth={2} />);

const fmtData = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
};

// FE-084 — l'elenco si carica a blocchi da 50 con «Carica altri» e il contatore
// «Mostrati N di M»; la ricerca per nome la fa il server (`?search=`), cosi'
// trova anche i documenti non ancora caricati. `limite` = anteprima fissa
// (es. «ultimi 5»), senza contatore.
export default function Storico({ onUpload, showToast, extraParams, compatto, onTogglePreferito, preferitiLocali, limite }) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]     = useState('');
  const [filterTipo, setFilterTipo]       = useState('');
  const [filterCantiere, setFilterCantiere] = useState('');
  const [filterCliente, setFilterCliente]   = useState('');
  const [cantieri, setCantieri] = useState([]);
  const [clienti, setClienti]   = useState([]);
  const [condividi, setCondividi]   = useState(null);
  const [firma, setFirma]           = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    cantieriApi.list({ page_size: 200 }).then(r => setCantieri(r.data.results || r.data)).catch(() => {});
    clientiApi.list({ page_size: 200 }).then(r => setClienti(r.data.results || r.data)).catch(() => {});
  }, []);

  // Ricerca: il server cerca dopo 350 ms di pausa nella digitazione (come il listino).
  useEffect(() => {
    const attesa = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(attesa);
  }, [searchInput]);

  const params = {};
  if (filterTipo)     params.tipo     = filterTipo;
  if (filterCantiere) params.cantiere = filterCantiere;
  if (filterCliente)  params.cliente  = filterCliente;
  if (search)         params.search   = search;
  // Parametri dal genitore (cartella, provenienza, tag, date, vista)
  Object.keys(extraParams || {}).forEach(function (k) {
    if (extraParams[k] !== undefined) params[k] = extraParams[k];
  });
  const elenco = useArchivioBlocchi(params, { limite: limite });
  const docs = elenco.docs;
  const filtriAttivi = !!(search || filterTipo || filterCantiere || filterCliente);

  const handleDelete = async (doc) => {
    try {
      await documentiApi.delete(doc.id);
      elenco.rimuovi(doc.id);
      setDeleteTarget(null);
      showToast('Documento eliminato', 'success');
    } catch {
      showToast('Errore eliminazione', 'error');
    }
  };

  const selSty = { padding: '7px 10px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 12, outline: 'none', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer' };

  return (
    <div>
      {/* Filtri — nascosti in modalita' compatta */}
      {!compatto && (
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Cerca per nome…"
          style={{ flex: 1, minWidth: 160, padding: '7px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 12, outline: 'none', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ds-text)' }}
        />
        <select style={selSty} value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}>
          {TIPI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select style={selSty} value={filterCantiere} onChange={(e) => setFilterCantiere(e.target.value)}>
          <option value="">Tutti i cantieri</option>
          {cantieri.map((c) => <option key={c.id} value={c.id}>{c.titolo}</option>)}
        </select>
        <select style={selSty} value={filterCliente} onChange={(e) => setFilterCliente(e.target.value)}>
          <option value="">Tutti i clienti</option>
          {clienti.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
      </div>
      )}

      {/* Contenuto */}
      {elenco.loading ? (
        <SkeletonLoader type="list" count={5} />
      ) : elenco.error ? (
        <div style={{ padding: 20, color: 'var(--ds-danger)', fontSize: 13 }}>
          {elenco.error} <button onClick={elenco.ricarica} style={{ marginLeft: 8, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>Riprova</button>
        </div>
      ) : docs.length === 0 && filtriAttivi ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', fontSize: 13, color: 'var(--ds-muted)' }}>
          Nessun documento corrisponde alla ricerca o ai filtri.
        </div>
      ) : docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ marginBottom: 12 }}><IconFolder size={48} /></div>
          <div style={{ fontWeight: 500, fontSize: 15, fontFamily: 'var(--font-display)', color: 'var(--ink)', marginBottom: 6 }}>Nessun documento ancora</div>
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginBottom: 20 }}>Carica il primo!</div>
          <button onClick={onUpload} style={{ padding: '9px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>+ Carica documento</button>
        </div>
      ) : (
        <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--ds-border)', background: 'var(--chip)' }}>
                {['', 'Nome', 'Tipo', 'Riferimento', 'Data', 'Dim.', 'Azioni'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => {
                const color = TIPO_ICON_COLOR[doc.tipo] || 'var(--ds-muted)';
                const preferito = statoPreferito(doc, preferitiLocali);
                const firmaOk = doc.tipo === 'contratto' || doc.tipo === 'preventivo';
                return (
                  <tr key={doc.id} className="table-row">
                    <td style={{ padding: '10px 12px', width: 32 }}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>{doc.tipo === 'foto' ? <IconImage size={18} color={color} /> : <IconFile size={18} color={color} />}</span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nome_file}</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span className="badge" style={{ background: tint(color, 14), color }}>{TIPO_LABEL[doc.tipo] || doc.tipo}</span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--ds-muted)' }}>
                      {doc.cantiere_nome && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconBuilding /> {doc.cantiere_nome}</div>}
                      {doc.cliente_nome  && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconUser /> {doc.cliente_nome}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>{fmtData(doc.data_upload)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>{doc.dimensione_bytes ? doc.dimensione_leggibile : (doc.scaricabile ? 'PDF' : '')}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {doc.file_url && (
                          <>
                            <button className="icon-btn" title="Anteprima" onClick={() => window.open(doc.file_url, '_blank')}><IconEye /></button>
                            <a href={doc.file_url} download={doc.nome_file} className="icon-btn" title="Scarica" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}><IconDownload /></a>
                          </>
                        )}
                        <button className="icon-btn" title="Condividi" onClick={() => setCondividi(doc)}><IconLink /></button>
                        {firmaOk && <button className="icon-btn" title="Firma" onClick={() => setFirma(doc)}><IconPen /></button>}
                        {onTogglePreferito && (
                          <button className="icon-btn" title={preferito ? 'Rimuovi preferito' : 'Aggiungi preferito'} onClick={() => onTogglePreferito(doc.id, preferito)} style={{ color: preferito ? 'var(--warn)' : undefined }}>
                            <IconStar filled={preferito} />
                          </button>
                        )}
                        <button className="icon-btn" title="Elimina" style={{ color: 'var(--ds-danger)' }} onClick={() => setDeleteTarget(doc)}><IconTrash /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!elenco.loading && !elenco.error && <CaricaAltri elenco={elenco} />}

      {/* Modal condivisione */}
      {condividi && <CondividiModal documento={condividi} onClose={() => setCondividi(null)} />}

      {/* Modal firma */}
      {firma && <FirmaDigitaleModal documento={firma} onClose={() => setFirma(null)} />}

      {/* Confirm delete */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 380, padding: '28px 28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Elimina documento</h3>
            <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--ds-muted)' }}>Vuoi eliminare <b>"{deleteTarget.nome_file}"</b>? Questa azione non è reversibile.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteTarget(null)} style={{ padding: '9px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--ds-text)' }}>Annulla</button>
              <button onClick={() => handleDelete(deleteTarget)} style={{ padding: '9px 18px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--ds-danger)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
